// Host-side collision, for feedback only: the testbed has no game over. A bullet hits when its
// hit circle touches the ship's; a laser when the ship comes within half its width of the beam.
// Only actors with CAN_HIT count, and each counts once per overlap, not once per tick.
import type { Bullets, Lasers } from './sim.ts';

const CAN_HIT = 4;

/** The ship's hit radius: the small dot it dodges with, as in most shmups. */
export const PLAYER_HIT = 2.5;

export class Collider {
  hits = 0;
  /** Ids touching the ship last tick, so an overlap only counts as it starts. */
  private touching = new Set<number>();
  private next = new Set<number>();

  reset(): void {
    this.hits = 0;
    this.touching.clear();
  }

  /** Checks one tick's actors against the ship at (px, py). Returns how many new hits. */
  check(view: Bullets, lasers: Lasers, px: number, py: number, radius: (kind: number) => number): number {
    const next = this.next;
    next.clear();
    let fresh = 0;
    for (let i = 0; i < view.count; i++) {
      if ((view.flags[i] & CAN_HIT) === 0) continue;
      const r = radius(view.kind[i]) + PLAYER_HIT;
      const dx = view.x[i] - px;
      const dy = view.y[i] - py;
      if (dx * dx + dy * dy >= r * r) continue;
      const id = view.ids[i];
      next.add(id);
      if (!this.touching.has(id)) fresh++;
    }
    for (let i = 0; i < lasers.count; i++) {
      if ((lasers.flags[i] & CAN_HIT) === 0) continue;
      const reach = lasers.width[i] / 2 + PLAYER_HIT;
      if (polylineDistance2(lasers.points, lasers.pointsStart[i], lasers.pointsLen[i], px, py) >= reach * reach)
        continue;
      const id = lasers.ids[i];
      next.add(id);
      if (!this.touching.has(id)) fresh++;
    }
    this.next = this.touching;
    this.touching = next;
    this.hits += fresh;
    return fresh;
  }
}

/** Squared distance from (px, py) to the nearest point of a polyline in a points buffer. */
function polylineDistance2(pts: Float32Array, start: number, len: number, px: number, py: number): number {
  let best = Infinity;
  for (let k = 0; k + 1 < len; k++) {
    const ax = pts[(start + k) * 2];
    const ay = pts[(start + k) * 2 + 1];
    const bx = pts[(start + k + 1) * 2];
    const by = pts[(start + k + 1) * 2 + 1];
    const vx = bx - ax;
    const vy = by - ay;
    const l2 = vx * vx + vy * vy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / l2)) : 0;
    const dx = ax + vx * t - px;
    const dy = ay + vy * t - py;
    best = Math.min(best, dx * dx + dy * dy);
  }
  return best;
}
